/**
 * Team Member Payment Service
 * Handles freelancer/contractor payments, COGS tracking, and vendor management
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type TeamMemberPayment = Database['public']['Tables']['team_member_payments']['Row'];
type ProjectTeamMember = Database['public']['Tables']['project_team_members']['Row'];

export interface PaymentRequest {
  team_member_id: string;
  project_id: string;
  amount: number;
  payment_method: string;
  reference_number?: string;
  invoice_number?: string;
  cost_category: 'labor' | 'materials' | 'travel' | 'equipment' | 'other';
  is_reimbursable: boolean;
  notes?: string;
}

export interface PaymentApproval {
  payment_id: string;
  approved: boolean;
  notes?: string;
}

export interface COGSAnalysis {
  project_id: string;
  total_team_costs: number;
  cost_by_category: Record<string, number>;
  cost_by_member_type: Record<string, number>;
  cost_per_hour: number;
  budget_utilization: number;
  projected_costs: number;
}

export interface VendorPerformance {
  team_member_id: string;
  total_payments: number;
  average_cost_per_hour: number;
  projects_worked: number;
  performance_rating: number;
  reliability_score: number;
}

class TeamMemberPaymentService {
  /**
   * Create payment request for team member
   */
  async createPaymentRequest(request: PaymentRequest): Promise<string> {
    try {
      // Validate team member exists and is active
      const { data: teamMember, error: memberError } = await supabase
        .from('project_team_members')
        .select('*')
        .eq('id', request.team_member_id)
        .eq('status', 'active')
        .single();

      if (memberError || !teamMember) {
        throw new Error('Team member not found or inactive');
      }

      // Check if payment exceeds budget (if applicable)
      const budgetCheck = await this.checkBudgetConstraints(
        request.project_id,
        request.team_member_id,
        request.amount
      );

      if (!budgetCheck.within_budget) {
        // Condition check only
      }

      // Create payment record
      const { data: payment, error } = await supabase
        .from('team_member_payments')
        .insert({
          team_member_id: request.team_member_id,
          project_id: request.project_id,
          amount: request.amount,
          payment_method: request.payment_method,
          reference_number: request.reference_number,
          invoice_number: request.invoice_number,
          cost_category: request.cost_category,
          is_reimbursable: request.is_reimbursable,
          notes: request.notes,
          status: request.amount > 1000 ? 'pending' : 'approved', // Auto-approve small amounts
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select('id')
        .single();

      if (error) throw error;

      // Log activity
      await this.logPaymentActivity(payment.id, 'created', request.amount);

      // Send notification if requires approval
      if (request.amount > 1000) {
        await this.sendApprovalNotification(payment.id, teamMember, request.amount);
      }

      return payment.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Approve or reject payment
   */
  async processPaymentApproval(approval: PaymentApproval): Promise<void> {
    try {
      const { data: payment, error: fetchError } = await supabase
        .from('team_member_payments')
        .select('*, team_member:project_team_members(*)')
        .eq('id', approval.payment_id)
        .single();

      if (fetchError) throw fetchError;

      const newStatus = approval.approved ? 'approved' : 'rejected';
      const currentUser = (await supabase.auth.getUser()).data.user;

      // Update payment status
      const { error: updateError } = await supabase
        .from('team_member_payments')
        .update({
          status: newStatus,
          approved_by: currentUser?.id,
          approved_at: new Date().toISOString(),
          notes: approval.notes
            ? `${payment.notes || ''}\n\nApproval: ${approval.notes}`
            : payment.notes,
        })
        .eq('id', approval.payment_id);

      if (updateError) throw updateError;

      // Log activity
      await this.logPaymentActivity(approval.payment_id, newStatus, payment.amount);

      // If approved, trigger payment processing
      if (approval.approved) {
        await this.processPayment(approval.payment_id);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process approved payment
   */
  async processPayment(paymentId: string): Promise<void> {
    try {
      // In a real implementation, this would integrate with payment systems
      // For now, we'll mark as paid and update the status

      const { error } = await supabase
        .from('team_member_payments')
        .update({
          status: 'paid',
          payment_date: new Date().toISOString().split('T')[0],
        })
        .eq('id', paymentId);

      if (error) throw error;

      // Get payment details for logging
      const { data: payment } = await supabase
        .from('team_member_payments')
        .select('amount')
        .eq('id', paymentId)
        .single();

      await this.logPaymentActivity(paymentId, 'paid', payment?.amount || 0);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get COGS analysis for project
   */
  async getCOGSAnalysis(projectId: string): Promise<COGSAnalysis> {
    try {
      // Get project budget
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('budget')
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      // Get all payments for project
      const { data: payments, error: paymentsError } = await supabase
        .from('team_member_payments')
        .select(
          `
          amount,
          cost_category,
          team_member:project_team_members (
            member_type,
            hourly_rate
          )
        `
        )
        .eq('project_id', projectId)
        .eq('status', 'paid');

      if (paymentsError) throw paymentsError;

      // Get total hours worked
      const { data: timeEntries, error: timeError } = await supabase
        .from('project_time_entries')
        .select('hours_logged')
        .eq('project_id', projectId);

      if (timeError) throw timeError;

      const totalHours = timeEntries?.reduce((sum, entry) => sum + entry.hours_logged, 0) || 0;
      const totalCosts = payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;

      // Analyze costs by category
      const costByCategory =
        payments?.reduce(
          (acc, payment) => {
            acc[payment.cost_category] = (acc[payment.cost_category] || 0) + payment.amount;
            return acc;
          },
          {} as Record<string, number>
        ) || {};

      // Analyze costs by member type
      const costByMemberType =
        payments?.reduce(
          (acc, payment) => {
            const memberType = payment.team_member.member_type;
            acc[memberType] = (acc[memberType] || 0) + payment.amount;
            return acc;
          },
          {} as Record<string, number>
        ) || {};

      const costPerHour = totalHours > 0 ? totalCosts / totalHours : 0;
      const budgetUtilization = project.budget > 0 ? (totalCosts / project.budget) * 100 : 0;

      // Project remaining costs based on burn rate
      const projectedCosts = await this.calculateProjectedCosts(projectId, totalCosts, totalHours);

      return {
        project_id: projectId,
        total_team_costs: totalCosts,
        cost_by_category: costByCategory,
        cost_by_member_type: costByMemberType,
        cost_per_hour: costPerHour,
        budget_utilization: budgetUtilization,
        projected_costs: projectedCosts,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get vendor performance metrics
   */
  async getVendorPerformance(teamMemberId: string): Promise<VendorPerformance> {
    try {
      // Get payment history
      const { data: payments, error: paymentsError } = await supabase
        .from('team_member_payments')
        .select('amount, project_id')
        .eq('team_member_id', teamMemberId)
        .eq('status', 'paid');

      if (paymentsError) throw paymentsError;

      // Get time entries
      const { data: timeEntries, error: timeError } = await supabase
        .from('project_time_entries')
        .select('hours_logged')
        .eq('team_member_id', teamMemberId);

      if (timeError) throw timeError;

      const totalPayments = payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
      const totalHours = timeEntries?.reduce((sum, entry) => sum + entry.hours_logged, 0) || 0;
      const projectsWorked = new Set(payments?.map((p) => p.project_id) || []).size;

      const averageCostPerHour = totalHours > 0 ? totalPayments / totalHours : 0;

      // Calculate performance metrics (simplified)
      const performanceRating = await this.calculatePerformanceRating(teamMemberId);
      const reliabilityScore = await this.calculateReliabilityScore(teamMemberId);

      return {
        team_member_id: teamMemberId,
        total_payments: totalPayments,
        average_cost_per_hour: averageCostPerHour,
        projects_worked: projectsWorked,
        performance_rating: performanceRating,
        reliability_score: reliabilityScore,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get payments requiring approval
   */
  async getPendingApprovals(organizationId?: string): Promise<any[]> {
    try {
      let query = supabase
        .from('team_member_payments')
        .select(
          `
          *,
          team_member:project_team_members (
            name,
            email,
            member_type
          ),
          project:projects (
            name,
            project_code,
            organization_id
          )
        `
        )
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (organizationId) {
        query = query.eq('project.organization_id', organizationId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Bulk approve payments
   */
  async bulkApprovePayments(paymentIds: string[], notes?: string): Promise<void> {
    try {
      const currentUser = (await supabase.auth.getUser()).data.user;

      const { error } = await supabase
        .from('team_member_payments')
        .update({
          status: 'approved',
          approved_by: currentUser?.id,
          approved_at: new Date().toISOString(),
          notes: notes,
        })
        .in('id', paymentIds);

      if (error) throw error;

      // Process payments
      for (const paymentId of paymentIds) {
        await this.processPayment(paymentId);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check budget constraints
   */
  private async checkBudgetConstraints(
    projectId: string,
    teamMemberId: string,
    amount: number
  ): Promise<{ within_budget: boolean; remaining_budget: number; utilization: number }> {
    const { data: project } = await supabase
      .from('projects')
      .select('budget')
      .eq('id', projectId)
      .single();

    const { data: member } = await supabase
      .from('project_team_members')
      .select('budget_allocation')
      .eq('id', teamMemberId)
      .single();

    const { data: existingPayments } = await supabase
      .from('team_member_payments')
      .select('amount')
      .eq('project_id', projectId)
      .eq('team_member_id', teamMemberId)
      .in('status', ['approved', 'paid']);

    const totalSpent = existingPayments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
    const budget = member?.budget_allocation || project?.budget || 0;
    const remainingBudget = budget - totalSpent;
    const utilization = budget > 0 ? ((totalSpent + amount) / budget) * 100 : 0;

    return {
      within_budget: amount <= remainingBudget,
      remaining_budget: remainingBudget,
      utilization: utilization,
    };
  }

  /**
   * Calculate projected costs
   */
  private async calculateProjectedCosts(
    projectId: string,
    currentCosts: number,
    currentHours: number
  ): Promise<number> {
    // Get project timeline
    const { data: project } = await supabase
      .from('projects')
      .select('start_date, end_date, estimated_hours')
      .eq('id', projectId)
      .single();

    if (!project?.end_date) return currentCosts;

    const totalEstimatedHours = project.estimated_hours || currentHours * 2;
    const remainingHours = Math.max(0, totalEstimatedHours - currentHours);
    const costPerHour = currentHours > 0 ? currentCosts / currentHours : 0;

    return currentCosts + remainingHours * costPerHour;
  }

  /**
   * Calculate performance rating
   */
  private async calculatePerformanceRating(teamMemberId: string): Promise<number> {
    // Simplified performance calculation
    // In a real implementation, this would consider multiple factors
    const { data: projects } = await supabase
      .from('project_team_members')
      .select('project:projects(status)')
      .eq('id', teamMemberId);

    const completedProjects = projects?.filter((p) => p.project.status === 'completed').length || 0;
    const totalProjects = projects?.length || 1;

    return (completedProjects / totalProjects) * 5; // 0-5 rating
  }

  /**
   * Calculate reliability score
   */
  private async calculateReliabilityScore(teamMemberId: string): Promise<number> {
    // Simplified reliability calculation
    // In a real implementation, this would consider on-time delivery, quality, etc.
    const { data: payments } = await supabase
      .from('team_member_payments')
      .select('created_at, payment_date')
      .eq('team_member_id', teamMemberId)
      .eq('status', 'paid');

    if (!payments?.length) return 5;

    // Calculate average payment processing time (days)
    const avgProcessingTime =
      payments.reduce((sum, payment) => {
        const created = new Date(payment.created_at);
        const paid = new Date(payment.payment_date || payment.created_at);
        return sum + (paid.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
      }, 0) / payments.length;

    // Score based on processing time (faster = higher score)
    return Math.max(1, 5 - avgProcessingTime / 10);
  }

  /**
   * Send approval notification
   */
  private async sendApprovalNotification(
    paymentId: string,
    teamMember: any,
    amount: number
  ): Promise<void> {
    // In a real implementation, this would send email/SMS notifications
    // Log activity
    await supabase.from('activities').insert({
      type: 'payment_approval_required',
      title: 'Payment Approval Required',
      description: `Payment of $${amount} for ${teamMember.name} requires approval`,
      related_type: 'payment',
      related_id: paymentId,
      created_by: (await supabase.auth.getUser()).data.user?.id,
    });
  }

  /**
   * Log payment activity
   */
  private async logPaymentActivity(
    paymentId: string,
    action: string,
    amount: number
  ): Promise<void> {
    try {
      await supabase.from('activities').insert({
        type: 'team_payment',
        title: `Payment ${action}`,
        description: `Team member payment of $${amount} ${action}`,
        related_type: 'payment',
        related_id: paymentId,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      });
    } catch (error) {
      // Error handling omitted intentionally
    }
  }
}

export const teamMemberPaymentService = new TeamMemberPaymentService();
export default teamMemberPaymentService;
